import { useGoogleReCaptcha } from '@google-recaptcha/react'
import InfoIcon from '@mui/icons-material/Info'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
  Alert,
  Button,
  ButtonProps,
  Checkbox,
  CircularProgress,
  circularProgressClasses,
  Popover,
  Typography,
} from '@mui/material'
import FormControl from '@mui/material/FormControl'
import InputBase from '@mui/material/InputBase'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import { styled } from '@mui/material/styles'
import { Stack } from '@mui/system'

import * as React from 'react'
import { useEffect, useRef, useState } from 'react'

import { useUserLocation } from '../../hooks'
import { ObstructionType, Trail } from '../../models'
import { ObstructionReportPayload } from '../../models/ObstructionReport.model'
import { ALERT_LEVEL } from '../../utils/constants'
import ImageUpload from '../ImageUpload/ImageUpload'
import LocationPermissionDialog from '../LocationPermissionDialog/LocationPermissionDialog'
import { TrailMap } from '../TrailMap/TrailMap'
import { useObstructionReportForm } from './useObstructionReportForm'

const BootstrapInput = styled(InputBase)(({ theme }) => ({
  '& .MuiInputBase-input': {
    '&:focus': {
      borderRadius: 100,
    },
    border: '1px solid #636363',
    borderRadius: 100,
    color: '#000',
    fontSize: 14,
    padding: '5px 26px 5px 16px',
    position: 'relative',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
  },
  '& .MuiSelect-icon': {
    color: '#636363',
    fontSize: 30,
    right: 10,
  },
  'label + &': {
    marginTop: theme.spacing(3),
  },
}))

const DescriptionInput = styled(InputBase)(({ theme }) => ({
  '& .MuiInputBase-input': {
    '&:focus': {
      borderRadius: 8,
    },
    backgroundColor: '#e1e1e1',
    border: '1px solid #e1e1e1',
    borderRadius: 8,
    color: '#000',
    fontSize: 14,
    padding: '8px 26px 8px 16px',
    position: 'relative',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
  },
  '& .MuiSelect-icon': {
    color: '#636363',
    fontSize: 30,
    right: 10,
  },
  'label + &': {
    marginTop: theme.spacing(3),
  },
}))

const ColorButton = styled(Button)<ButtonProps>(() => ({
  '&:hover': {
    backgroundColor: '#e1e1e1',
    borderColor: '#636363',
  },
  backgroundColor: '#fff',
  border: '1px solid #636363',
  borderRadius: 100,
  color: '#636363',
  fontWeight: 700,
  letterSpacing: '0.03rem',
  textTransform: 'none',
}))

const SubmitButton = styled(Button)<ButtonProps>(() => ({
  '&:disabled': {
    backgroundColor: '#e2e2e2',
    borderColor: '#e2e2e2',
    color: '#636363',
  },
  '&:hover': {
    backgroundColor: 'darkGrey',
    borderColor: '#636363',
  },
  backgroundColor: '#000',
  borderRadius: 100,
  color: '#fff',
  fontWeight: 700,
  letterSpacing: '0.03rem',
  textTransform: 'none',
}))

interface ReportIssueFormProps {
  toggleShowReportIssue: () => void
  trail: Trail
  refetchData: () => void
  obstructionTypes?: ObstructionType[]
  hasTrailMap?: boolean
}

export default function ReportIssueForm(props: ReportIssueFormProps) {
  const { toggleShowReportIssue, trail, refetchData, obstructionTypes, hasTrailMap } = props
  const [issue, setIssue] = useState<number | undefined>(undefined)
  const [issueDescription, setIssueDescription] = useState('')
  const [description, setDescription] = useState('')
  const [email, setEmail] = useState('')
  const [fileName, setFileName] = useState('')
  const [alertLevel, setAlertLevel] = useState('')
  const [useCurrentLocation, setUseCurrentLocation] = useState(false)
  const [image, setImage] = useState<string | ArrayBuffer | null>()
  const [isFormSubmited, setIsFormSubmited] = useState(false)
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [error, setError] = useState<string>()
  const [isImageProcessing, setIsImageProcessing] = useState(false)
  const [openLocationDialog, setOpenLocationDialog] = useState(false)
  const [usePinLocation, setUsePinLocation] = useState(false)
  const [pinLocation, setPinLocation] = useState<{ lat: number; lng: number } | null>(null)
  const [pinLocationConfirmed, setPinLocationConfirmed] = useState(false)
  const pinSectionRef = useRef<HTMLDivElement>(null)
  const successRef = useRef<HTMLDivElement>(null)
  const id = anchorEl ? 'simple-popover' : undefined
  const userLocation = useUserLocation()
  const {
    mutate: submitForm,
    data: formResponse,
    isPending: isFormLoading,
    error: formError,
    isError: isFormError,
  } = useObstructionReportForm()

  useEffect(() => {
    if (isFormError) {
      setError(formError.message)
    }
    if (formResponse) {
      refetchData()
      setIsFormSubmited(true)
      setAlertLevel('')
      setIssue(undefined)
      setIssueDescription('')
      setDescription('')
      setEmail('')
      setUseCurrentLocation(false)
      setUsePinLocation(false)
      setPinLocation(null)
      setPinLocationConfirmed(false)
      setImage(null)
      setFileName('')
    }
  }, [formResponse, isFormLoading])

  useEffect(() => {
    const previewOpening = usePinLocation || (useCurrentLocation && hasTrailMap)
    if (previewOpening && pinSectionRef.current) {
      pinSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [usePinLocation, useCurrentLocation, hasTrailMap])

  useEffect(() => {
    if (isFormSubmited && successRef.current) {
      successRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [isFormSubmited])

  const { executeV3 } = useGoogleReCaptcha()

  const handleChange = (event: { target: { value: string } }) => {
    setIssue(Number(event.target.value))
  }
  const handleAlertLevelChange = (event: { target: { value: string } }) => {
    setAlertLevel(event.target.value)
  }

  const handleSubmit = async () => {
    setError(undefined)
    if (issue === undefined) {
      setError('Please select an issue')
      return
    }
    const currentLocationReady = hasTrailMap
      ? useCurrentLocation && pinLocationConfirmed
      : useCurrentLocation && !!userLocation.userCoords
    const hasLocation = currentLocationReady || (usePinLocation && pinLocationConfirmed)
    if (description === '' && !hasLocation) {
      setError('Location required. Please either check a location option or add details in the description to proceed.')
      return
    }
    if (executeV3) {
      const token = await executeV3('submit')

      const mapCoordsConfirmed = (useCurrentLocation || usePinLocation) && pinLocation !== null && pinLocationConfirmed
      const rawCurrentCoords = useCurrentLocation && !hasTrailMap ? userLocation.userCoords : undefined

      const payload: ObstructionReportPayload = {
        alertLevel: alertLevel === '' ? undefined : alertLevel.toLowerCase(),
        gpsLat: mapCoordsConfirmed ? pinLocation!.lat : (rawCurrentCoords?.lat ?? 0),
        gpsLong: mapCoordsConfirmed ? pinLocation!.lng : (rawCurrentCoords?.long ?? 0),
        imageFile: image?.toString() ?? '',
        obstructionDescription: description,
        obstructionType: issue,
        reporterEmail: email === '' ? undefined : email,
        statusDescription: obstructionTypes?.some(
          (obstructionType) =>
            obstructionType.id === issue && ['other', 'others'].includes(obstructionType.name.toLowerCase()),
        )
          ? issueDescription
          : '',
        token: token,
        trailId: trail.id,
      }
      submitForm(payload)
    } else {
      console.error('Google Recaptcha not available for execution.')
      setError('Failed to load reCAPTCHA. Please refresh and try again.')
    }
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleLocationPermissions = async (checked: boolean) => {
    if (checked) {
      setUsePinLocation(false)
      setPinLocation(null)
      setPinLocationConfirmed(false)
    }
    if (!checked) {
      setUseCurrentLocation(false)
      setPinLocation(null)
      setPinLocationConfirmed(false)
      return
    }
    if (userLocation.userCoords?.long && userLocation.userCoords?.lat) {
      setUseCurrentLocation(true)
      return
    }
    try {
      await userLocation.requestUserCoords()
      setUseCurrentLocation(true)
    } catch {
      setOpenLocationDialog(true)
    }
  }

  const handlePinLocationToggle = (checked: boolean) => {
    setUsePinLocation(checked)
    setPinLocationConfirmed(false)
    setPinLocation(null)
    if (checked) {
      setUseCurrentLocation(false)
    }
  }

  const renderMapConfirmation = (caption: string, initialCenter?: [number, number]) =>
    !pinLocationConfirmed ? (
      <Stack gap={0.5}>
        <Typography variant="caption" color="#636363">
          {caption}
        </Typography>
        <TrailMap
          trailId={trail.id}
          onLocationSelect={(lat, lng) => setPinLocation({ lat, lng })}
          height={300}
          initialCenter={initialCenter}
        />
        <ColorButton
          variant="outlined"
          disabled={pinLocation === null}
          onClick={() => setPinLocationConfirmed(true)}
          sx={{ fontSize: { md: '14px', xs: '11px' }, padding: { md: '5px 16px', xs: '5px 8px' } }}
          fullWidth
        >
          Confirm location
        </ColorButton>
      </Stack>
    ) : (
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography variant="caption" color="#636363" sx={{ flex: 1 }}>
          📍 {pinLocation!.lat.toFixed(5)}, {pinLocation!.lng.toFixed(5)}
        </Typography>
        <ColorButton
          variant="outlined"
          onClick={() => setPinLocationConfirmed(false)}
          sx={{ fontSize: { md: '12px', xs: '10px' }, padding: { md: '3px 12px', xs: '3px 8px' } }}
        >
          Change
        </ColorButton>
      </Stack>
    )

  return (
    <>
      {isFormSubmited ? (
        <Stack
          direction="column"
          alignItems="center"
          gap={2}
          ref={successRef}
          style={{ marginBottom: 30, marginTop: 10 }}
        >
          <Typography variant="h5" fontWeight={700} textAlign="center" color="#636363" paddingX={1} gutterBottom>
            Thank you for keeping
            <br /> our trails rad!
          </Typography>
          <ColorButton
            variant="outlined"
            role={undefined}
            sx={{
              fontSize: { md: '16px', xs: '14px' },
              fontWeight: 700,
              padding: { md: '5px 40px 5px 40px', xs: '5px 25px 5px 25px' },
            }}
            onClick={() => setIsFormSubmited(false)}
          >
            Report another issue
          </ColorButton>
        </Stack>
      ) : (
        <Stack gap={2} style={{ marginBottom: 30, marginTop: 10 }}>
          <FormControl variant="standard" fullWidth>
            <Select
              labelId="demo-customized-select-label"
              id="demo-customized-select"
              value={issue?.toString() ?? ''}
              onChange={handleChange}
              input={<BootstrapInput />}
              displayEmpty
              inputProps={{
                MenuProps: {
                  MenuListProps: {
                    sx: {
                      backgroundColor: '#ffffff',
                      color: '#000',
                    },
                  },
                },
                'aria-label': 'Without label',
              }}
              IconComponent={KeyboardArrowDownIcon}
            >
              <MenuItem value="" disabled>
                <span style={{ color: '#636363' }}>Select an issue...</span>
              </MenuItem>
              {obstructionTypes?.map((obstructionType) => (
                <MenuItem key={obstructionType.id} value={obstructionType.id}>
                  {obstructionType.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {
            // Find the obstructionType by the selected issue ID and check its name
            obstructionTypes?.some(
              (obstructionType) =>
                obstructionType.id === issue && ['other', 'others'].includes(obstructionType.name.toLowerCase()),
            ) && (
              <DescriptionInput
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIssueDescription(e.target.value)}
                placeholder="Issue details"
                value={issueDescription}
                inputProps={{ maxLength: 50 }}
              />
            )
          }
          <FormControl variant="standard" fullWidth style={{ position: 'relative' }}>
            <Select
              labelId="demo-customized-select-label"
              id="demo-customized-select"
              value={alertLevel}
              onChange={handleAlertLevelChange}
              input={<BootstrapInput />}
              displayEmpty
              inputProps={{
                MenuProps: {
                  MenuListProps: {
                    sx: {
                      backgroundColor: '#ffffff',
                      color: '#000',
                    },
                  },
                },
                'aria-label': 'Without label',
              }}
              IconComponent={KeyboardArrowDownIcon}
            >
              <MenuItem value="" disabled>
                <span style={{ color: '#636363' }}>Select an alert level...</span>
              </MenuItem>
              {ALERT_LEVEL.map((alertLevel) => (
                <MenuItem key={alertLevel} value={alertLevel}>
                  {alertLevel}
                </MenuItem>
              ))}
            </Select>
            <Typography
              marginLeft={0.5}
              variant="caption"
              color="#636363"
              fontWeight={400}
              style={{ cursor: 'pointer' }}
              id={id}
              alignItems="center"
              display="flex"
              mt={0.5}
            >
              Alert Info
              <InfoIcon sx={{ color: '#636363', fontSize: '16px', ml: 0.5 }} onClick={handleClick} />
            </Typography>
            <Popover
              id={id}
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                horizontal: 'left',
                vertical: 'bottom',
              }}
              sx={{
                '& .MuiPopover-paper': {
                  padding: '5px',
                  width: { md: '45%', sm: '65%', xs: '95%' },
                },
              }}
            >
              <Typography variant="caption" sx={{ padding: { sm: '20px', xs: '10px' } }}>
                <ul>
                  <li>
                    <span style={{ fontWeight: 700 }}> High Alert </span>- Serious issue requiring immediate attention.
                    Major safety concern or trail blockage.
                  </li>
                  <li>
                    <span style={{ fontWeight: 700 }}>Medium Alert</span> - Notable problem that needs addressing soon.
                    Affects ride quality or safety, but not an emergency.
                  </li>
                  <li>
                    <span style={{ fontWeight: 700 }}>Low Alert</span> - Minor issue worth reporting. Not urgent but
                    should be on maintenance radar.
                  </li>
                </ul>
              </Typography>
            </Popover>
          </FormControl>
          <ImageUpload
            fileName={fileName}
            setFileName={setFileName}
            setImage={setImage}
            isProcessing={isImageProcessing}
            setIsProcessing={setIsImageProcessing}
          />
          <Stack>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Description
            </Typography>
            <DescriptionInput
              type="text"
              placeholder="Please help the trail crew know where to go to address this report..."
              value={description}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
              multiline
              rows={3}
              inputProps={{ maxLength: 200 }}
            />
          </Stack>
          <Stack direction="column" gap={1} ref={pinSectionRef}>
            <Stack direction="row" alignItems="center">
              <Checkbox
                checked={useCurrentLocation}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleLocationPermissions(e.target.checked)}
                sx={{ '&.Mui-checked': { color: '#000' }, color: '#000' }}
                style={{ padding: 0 }}
              />
              <Typography variant="subtitle2" fontWeight={700} marginLeft={1}>
                Use your current location as the reporting location
              </Typography>
            </Stack>
            {hasTrailMap && (
              <Stack direction="row" alignItems="center">
                <Checkbox
                  checked={usePinLocation}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePinLocationToggle(e.target.checked)}
                  sx={{ '&.Mui-checked': { color: '#000' }, color: '#000' }}
                  style={{ padding: 0 }}
                />
                <Typography variant="subtitle2" fontWeight={700} marginLeft={1}>
                  Drop a pin on the map
                </Typography>
              </Stack>
            )}
            {hasTrailMap &&
              useCurrentLocation &&
              userLocation.userCoords &&
              renderMapConfirmation('Pan the map to confirm your location', [
                userLocation.userCoords.lat,
                userLocation.userCoords.long,
              ])}
            {hasTrailMap && usePinLocation && renderMapConfirmation('Pan the map to position the pin')}
          </Stack>
          <Stack>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Email address
              <Typography marginLeft={0.5} variant="caption" color="#636363" fontWeight={400}>
                (optional)
              </Typography>
            </Typography>
            <DescriptionInput
              value={email}
              placeholder="john@johndoe.com"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              type="email"
            />
          </Stack>
          {error && (
            <Alert
              icon={<WarningAmberIcon fontSize="inherit" style={{ alignSelf: 'center', color: '#ffffff' }} />}
              sx={{ bgcolor: 'rgb(211, 47, 47)', color: '#ffffff' }}
              severity="error"
              onClose={() => setError(undefined)}
            >
              {error}
            </Alert>
          )}
          <Stack direction="row" alignItems="center" spacing={2}>
            <ColorButton
              variant="outlined"
              tabIndex={-1}
              role={undefined}
              onClick={toggleShowReportIssue}
              sx={{
                fontSize: { md: '14px', xs: '11px' },
                padding: { md: '5px 16px 5px 16px', xs: '5px 2px 5px 2px' },
              }}
              fullWidth
            >
              Cancel
            </ColorButton>
            <SubmitButton
              variant="contained"
              tabIndex={-1}
              role={undefined}
              onClick={handleSubmit}
              sx={{
                fontSize: { md: '14px', xs: '11px' },
                padding: { md: '8px 16px 8px 16px', xs: '6px 2px 6px 2px' },
              }}
              fullWidth
              disabled={isFormLoading || isImageProcessing}
            >
              Submit
              {isFormLoading && (
                <CircularProgress
                  sx={(theme) => ({
                    color: '#636363',
                    [`& .${circularProgressClasses.circle}`]: {
                      strokeLinecap: 'round',
                    },
                    ...theme.applyStyles('dark', {
                      color: '#636363',
                    }),
                  })}
                  size={20}
                  style={{ marginLeft: 10 }}
                />
              )}
            </SubmitButton>
          </Stack>
        </Stack>
      )}
      <LocationPermissionDialog open={openLocationDialog} onClose={() => setOpenLocationDialog(false)} />
    </>
  )
}
